import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowRight, CheckCircle2, Search } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Handles the legacy enobox.eu QR format: ?QR=A1&D=220621&N=000000
// - QR = model code (Enobox A1/A4/A8...)
// - D = production date
// - N = serial number
export default function QrLanding() {
  const [params] = useSearchParams();
  const modelCode = params.get("QR") || "";
  const prodDate = params.get("D") || "";
  const rawSerial = params.get("N") || "";

  const [disp, setDisp] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [manualSerial, setManualSerial] = useState(rawSerial);
  const [form, setForm] = useState({ title: "", description: "", reported_by: "", reporter_contact: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState("");

  const tryLookup = async (serial) => {
    if (!serial) return;
    setNotFound(false);
    setErr("");
    // Try reasonable variants. Wineemotion convention: serial = D + N (e.g. 220621000000)
    const variants = Array.from(new Set([
      prodDate && serial ? `${prodDate}${serial}` : null,
      serial,
      serial.replace(/^0+/, ""),
      modelCode && serial ? `${modelCode}-${serial}` : null,
      modelCode && serial ? `${modelCode}${serial}` : null,
    ].filter(Boolean)));
    for (const v of variants) {
      try {
        const { data } = await axios.get(`${API}/public/dispenser/${encodeURIComponent(v)}`);
        setDisp({ ...data, matched_variant: v });
        return;
      } catch (lookupErr) {
        // Variant not found — try next one. Log for debugging only.
        if (lookupErr?.response?.status !== 404) {
          console.debug(`QR lookup variant "${v}" failed:`, lookupErr?.message);
        }
      }
    }
    setNotFound(true);
  };

  useEffect(() => { if (rawSerial) tryLookup(rawSerial); /* eslint-disable-next-line */ }, [rawSerial]);

  const submit = async (e) => {
    e.preventDefault();
    if (!disp) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/public/tickets`, {
        ...form,
        dispenser_serial: disp.serial_number,
      });
      setDone(data);
    } catch (er) {
      setErr(er?.response?.data?.detail || "Errore nell'invio");
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-600 mb-4" strokeWidth={1.5} />
          <h1 className="font-display text-3xl font-bold text-zinc-950 mb-2">Ticket inviato!</h1>
          <div className="text-sm text-zinc-500">Numero riferimento: <span className="font-mono text-zinc-900">{done.ticket_number}</span></div>
          <p className="text-sm text-zinc-600 mt-3">Un tecnico Wineemotion prenderà in carico la richiesta al più presto.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <div className="max-w-lg mx-auto p-6 sm:p-10">
        <div className="flex items-center gap-2 mb-8">
          <BrandLogo size="xl" />
          <div>
            <div className="font-display font-bold text-zinc-950 leading-none">Wineemotion · Assistenza</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">Apri un ticket</div>
          </div>
        </div>

        {disp && (
          <div className="border border-zinc-200 rounded-sm bg-white p-4 mb-6" data-testid="qr-dispenser-found">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Erogatore rilevato dal QR</div>
            <div className="mt-1 font-mono text-sm text-zinc-900">{disp.serial_number}</div>
            <div className="text-sm font-medium text-zinc-900">{disp.model}</div>
            {disp.customer_name && (
              <div className="text-xs text-zinc-500 mt-1">
                {disp.customer_name}{disp.location ? ` · ${disp.location}` : ""}
              </div>
            )}
          </div>
        )}

        {!disp && !notFound && (
          <div className="border border-zinc-200 bg-white rounded-sm p-4 mb-6">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Non hai il QR? Inserisci il seriale</div>
            <div className="flex gap-2">
              <Input value={manualSerial} onChange={(e) => setManualSerial(e.target.value)} placeholder="es. 220621000000" className="rounded-sm" data-testid="manual-serial-input" />
              <Button onClick={() => tryLookup(manualSerial)} className="bg-brand hover:bg-brand-dark text-white rounded-sm" data-testid="manual-serial-lookup"><Search className="h-4 w-4" /></Button>
            </div>
          </div>
        )}
        {notFound && (
          <div className="border border-amber-200 bg-amber-50 rounded-sm p-4 mb-6">
            <div className="text-sm text-amber-900 mb-3">
              Erogatore <span className="font-mono">{rawSerial}</span>{modelCode ? ` (modello ${modelCode})` : ""} non trovato nel sistema.
              Puoi inserire manualmente il numero seriale stampato sul dispenser:
            </div>
            <div className="flex gap-2">
              <Input value={manualSerial} onChange={(e) => setManualSerial(e.target.value)} className="rounded-sm" placeholder="Numero seriale" data-testid="qr-manual-serial" />
              <Button onClick={() => tryLookup(manualSerial)} className="bg-brand hover:bg-brand-dark text-white rounded-sm" data-testid="qr-manual-lookup">
                <Search className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {disp && (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Il tuo nome *</Label>
              <Input required data-testid="qr-name" value={form.reported_by} onChange={(e) => setForm({ ...form, reported_by: e.target.value })} className="mt-1 rounded-sm h-11" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Telefono / email</Label>
              <Input data-testid="qr-contact" value={form.reporter_contact} onChange={(e) => setForm({ ...form, reporter_contact: e.target.value })} className="mt-1 rounded-sm h-11" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Cosa non funziona? *</Label>
              <Input required data-testid="qr-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 rounded-sm h-11" placeholder="es. Il vino esce lentamente" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Descrizione dettagliata *</Label>
              <Textarea required data-testid="qr-description" rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-sm" />
            </div>
            {err && <div className="text-sm text-red-700">{err}</div>}
            <Button type="submit" data-testid="qr-submit" disabled={loading} className="w-full h-11 bg-brand hover:bg-brand-dark text-white rounded-sm font-medium">
              {loading ? "Invio..." : "Invia richiesta"} <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </form>
        )}

        {!disp && !notFound && !rawSerial && (
          <div className="text-sm text-zinc-500">Nessun numero seriale nel link. Contatta l'assistenza al numero verde.</div>
        )}

        {prodDate && (
          <div className="mt-8 text-[10px] uppercase tracking-widest text-zinc-400">
            QR scansionato: modello <span className="font-mono">{modelCode || "?"}</span> · produzione <span className="font-mono">{prodDate}</span>
          </div>
        )}
      </div>
    </div>
  );
}
